import { CROPS, type CropId } from '@/data/crops';
import { ALL_FISH, type FishId, isFishId } from '@/data/fish';
import {
  BAITS,
  type BaitId,
  MATERIALS,
  type MaterialId,
  RODS,
  type RodId,
  isBaitId,
  isMaterialId,
  isRodId,
} from '@/data/tackle';
import { FURNITURE, type FurnitureId } from '@/world/furniture';
import { WEAPONS, type WeaponId } from '@/data/weapons';

/**
 * Everything that can sit in a slot.
 *
 * Tools, seeds and produce share one item type so the hotbar, inventory and
 * save format do not need three parallel implementations. What an item *does*
 * is a `use` kind the farming code switches on, not a subclass.
 */

export type ItemId =
  | 'hoe'
  | 'wateringCan'
  | 'feed'
  | 'egg'
  | RodId
  | WeaponId
  | `bait:${BaitId}`
  | `mat:${MaterialId}`
  | `seed:${CropId}`
  | `crop:${CropId}`
  | `fish:${FishId}`
  | `furniture:${FurnitureId}`;

export type ItemKind =
  | 'tool'
  | 'weapon'
  | 'seed'
  | 'produce'
  | 'furniture'
  | 'rod'
  | 'bait'
  | 'material';

export interface ItemDefinition {
  readonly id: ItemId;
  readonly name: string;
  readonly kind: ItemKind;
  /** Tools are unique and never stack; produce and seeds do. */
  readonly stackSize: number;
  /** Short label for the hotbar, where there is no room for a sprite yet. */
  readonly label: string;
  readonly tint: string;
  readonly sellPrice?: number;
}

const TOOLS: ItemDefinition[] = [
  { id: 'hoe', name: 'Hoe', kind: 'tool', stackSize: 1, label: 'Hoe', tint: '#c9a06a' },
  {
    id: 'wateringCan',
    name: 'Watering Can',
    kind: 'tool',
    stackSize: 1,
    label: 'Can',
    tint: '#5fc3e8',
  },
  { id: 'feed', name: 'Feed', kind: 'produce', stackSize: 99, label: 'Fd', tint: '#c9a06a' },
  {
    id: 'egg',
    name: 'Egg',
    kind: 'produce',
    stackSize: 99,
    label: 'Egg',
    tint: '#e9e2d6',
    sellPrice: 55,
  },
];

function seedsAndProduce(): ItemDefinition[] {
  const items: ItemDefinition[] = [];
  for (const crop of Object.values(CROPS)) {
    items.push({
      id: `seed:${crop.id}`,
      name: `${crop.name} Seeds`,
      kind: 'seed',
      stackSize: 99,
      label: crop.name.slice(0, 3),
      tint: '#8fd6a0',
      sellPrice: Math.max(1, Math.round(crop.sellPrice / 3)),
    });
    items.push({
      id: `crop:${crop.id}`,
      name: crop.name,
      kind: 'produce',
      stackSize: 99,
      label: crop.name.slice(0, 3),
      tint: '#f2c66d',
      sellPrice: crop.sellPrice,
    });
  }
  for (const fish of ALL_FISH) {
    items.push({
      id: `fish:${fish.id}` as ItemId,
      name: fish.name,
      kind: 'produce',
      stackSize: 99,
      label: fish.name.slice(0, 3),
      tint: '#5fc3e8',
      sellPrice: fish.sellPrice,
    });
  }

  for (const rod of Object.values(RODS)) {
    items.push({
      id: rod.id,
      name: rod.name,
      kind: 'rod',
      // Rods do not stack: you own one of each, and the best is the one you use.
      stackSize: 1,
      label: rod.name.split(' ')[0]!.slice(0, 4),
      tint: '#8fd6a0',
    });
  }

  for (const weapon of Object.values(WEAPONS)) {
    items.push({
      id: weapon.id,
      name: weapon.name,
      kind: 'weapon',
      // One of each, like every other tool: the best is the one you carry.
      stackSize: 1,
      label: weapon.name.split(' ')[0]!.slice(0, 4),
      tint: '#c8ced4',
    });
  }

  for (const bait of Object.values(BAITS)) {
    items.push({
      id: `bait:${bait.id}` as ItemId,
      name: bait.name,
      kind: 'bait',
      // Lures are kept, so one is enough; bait is spent by the cast.
      stackSize: bait.reusable ? 1 : 99,
      label: bait.name.slice(0, 3),
      tint: bait.tint,
    });
  }

  for (const material of Object.values(MATERIALS)) {
    items.push({
      id: `mat:${material.id}` as ItemId,
      name: material.name,
      kind: 'material',
      stackSize: 99,
      label: material.name.slice(0, 3),
      tint: material.tint,
    });
  }

  for (const piece of Object.values(FURNITURE)) {
    items.push({
      id: `furniture:${piece.id}`,
      name: piece.name,
      kind: 'furniture',
      stackSize: 9,
      label: piece.name.slice(0, 3),
      tint: '#c79bea',
    });
  }

  return items;
}

export const ITEMS: Readonly<Record<ItemId, ItemDefinition>> = Object.fromEntries(
  [...TOOLS, ...seedsAndProduce()].map((item) => [item.id, item]),
) as Record<ItemId, ItemDefinition>;

export function isItemId(value: unknown): value is ItemId {
  return typeof value === 'string' && value in ITEMS;
}

/** The fish an item represents, if it is one. */
export function fishOf(id: ItemId): FishId | null {
  const [kind, fish] = id.split(':');
  return kind === 'fish' && fish && isFishId(fish) ? fish : null;
}

/** The furniture an item places, if it is one. */
export function furnitureOf(id: ItemId): FurnitureId | null {
  const [kind, piece] = id.split(':');
  return kind === 'furniture' && piece && piece in FURNITURE
    ? (piece as FurnitureId)
    : null;
}

export function fishItem(id: FishId): ItemId {
  return `fish:${id}`;
}

export function furnitureItem(id: FurnitureId): ItemId {
  return `furniture:${id}`;
}

export function baitOf(id: ItemId): BaitId | null {
  const [kind, bait] = id.split(':');
  return kind === 'bait' && bait && isBaitId(bait) ? bait : null;
}

export function materialOf(id: ItemId): MaterialId | null {
  const [kind, material] = id.split(':');
  return kind === 'mat' && material && isMaterialId(material) ? material : null;
}

export function rodOf(id: ItemId): RodId | null {
  return isRodId(id) ? id : null;
}

export function baitItem(id: BaitId): ItemId {
  return `bait:${id}`;
}

export function materialItem(id: MaterialId): ItemId {
  return `mat:${id}`;
}

/** The crop a seed plants, or the crop a piece of produce came from. */
export function cropOf(id: ItemId): CropId | null {
  const [kind, crop] = id.split(':');
  if ((kind === 'seed' || kind === 'crop') && crop && crop in CROPS) {
    return crop as CropId;
  }
  return null;
}

export function seedFor(crop: CropId): ItemId {
  return `seed:${crop}`;
}

export function produceFor(crop: CropId): ItemId {
  return `crop:${crop}`;
}
